import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  TextContainer,
  Title,
  Subtitle,
  CardsContainer,
  Card,
  CardImage,
  CardText,
  CardTitle,
  CardTitleLine,
  Icon,
  IconContainer,
  CardLowerContainer,
  InteractiveContainer,
  MobileScrollButtonsContainer,
  DesktopScrollButtonsContainer,
} from "./styles";
import ScrollContainer from "react-indiana-drag-scroll";
import DiagonalArrow from "../../assets/images/diagonal_arrow.svg";
import RightArrow from "../../assets/images/right_arrow_short.svg";
import ArrowButton, {
  ArrowButtonType,
} from "../shared/ArrowButton";

import KristiFunkImage from "../../assets/images/clean_ingredients/clean-ingredients_experts_Kristi-Funk.png";
import RobertJacklerImage from "../../assets/images/clean_ingredients/clean-ingredients_experts_Robert-Jackler.png";
import JarredVImage from "../../assets/images/clean_ingredients/clean-ingredients_experts_Jarred-V.png";
import JoyBauerImage from "../../assets/images/clean_ingredients/clean-ingredients_experts_Joy-Bauer.png";
import DrMattImage from "../../assets/images/clean_ingredients/clean-ingredients_experts_Dr-Matt-Leaderman.png";


const defaultTitle = "The Experts Eat Beyond";
const defaultSubtitle =
  "The benefit of consuming Clean, Simple Ingredients is Feeling Good from the Inside Out. Hear from the experts themselves. ";
const defaultBackgroundColor = "#FDF2D6";
const defaultCardsBackgroundColor = "var(--color-green-90)";
const defaultCardsAcBackgroundColor = "#FDC04E";
const defaultColor = "var(--color-green-90)";
const defaultCards = [
  {
    id: "1",
    image: KristiFunkImage,
    text: "Beyond’s protein sources are extremely healthful foods - peas, lentils, brown rice, and faba beans.",
    fullName: "Dr. Kristi Funk",
    role: "MD",
    href: "/whats-new/breast-health-four-do-s",
    linkIsExternal: false,
  },
  {
    id: "2",
    image: JarredVImage,
    text: "Eating plant-based allowed me to play the game at a high-level, not feel weighed down, and be explosive on the court. My favorite Beyond product is the Beyond Steak. I love it because it's clean protein and it tastes great.",
    fullName: "Jarred Vanderbilt",
    role: "Pro Athlete",
    href: "https://www.instagram.com/jvando_/",
    linkIsExternal: true,
  },
  {
    id: "3",
    image: RobertJacklerImage,
    text: "The meat lobby wants to make plant-based meat less attractive to consumers. They're afraid their industry is going to be disrupted.",
    fullName: "Dr. Robert K. Jackler",
    role: "MD",
    href: "https://med.stanford.edu/profiles/robert-jackler",
    linkIsExternal: true,
  },
  {
    id: "4",
    image: JoyBauerImage,
    text: "They're now putting in heart-healthy avocado oil. It has a simple list of ingredients. It's 21 grams of protein.",
    fullName: "Joy Bauer",
    role: "MS, RDN",
    href: "https://www.instagram.com/joybauer/?hl=en",
    linkIsExternal: true,
  },
  {
    id: "5",
    image: DrMattImage,
    text: "Beyond plays a pivotal role in helping people make healthy lifestyle changes. In good faith I can recommend to patients, “When you have that craving for burgers, go eat Beyond.“",
    fullName: "Dr. Matt Lederman",
    role: "MD",
    href: "https://www.instagram.com/theconnectiondocs/",
    linkIsExternal: true,
  },
];

export type VideoCardsProps = {
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
  cardsBackgroundColor?: string;
  cardsAcBackgroundColor?: string;
  color?: string;
  cards?: {
    id: string;
    image: string;
    text: string;
    fullName: string;
    role: string;
    href: string;
    linkIsExternal?: boolean;
  }[];
};

const VideoCardsCarousel = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  backgroundColor = defaultBackgroundColor,
  cardsBackgroundColor = defaultCardsBackgroundColor,
  cardsAcBackgroundColor = defaultCardsAcBackgroundColor,
  color = defaultColor,
  cards = defaultCards,
}: VideoCardsProps): JSX.Element => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [leftArrowDisabled, setLeftArrowDisabled] =
    useState(true);
  const [rightArrowDisabled, setRightArrowDisabled] =
    useState(false);

  const handleScroll = useCallback(
    (direction: "left" | "right") => {
      if (!scrollerRef.current) return;

      const container = scrollerRef.current;
      const cards = Array.from(container.children);
      const containerRect =
        container.getBoundingClientRect();

      const firstVisibleCard = cards.find((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter =
          cardRect.left + cardRect.width / 2;
        const containerCenter =
          containerRect.left + containerRect.width / 2;

        return (
          Math.abs(cardCenter - containerCenter) <
          cardRect.width
        );
      });

      if (!firstVisibleCard) return;

      const targetCard =
        direction === "left"
          ? firstVisibleCard.previousElementSibling
          : firstVisibleCard.nextElementSibling;

      if (!targetCard) return;

      const gapSize = parseInt(
        getComputedStyle(container).gap || "0",
      );

      const targetRect = targetCard.getBoundingClientRect();
      const containerScrollLeft = container.scrollLeft;
      const targetLeft =
        targetRect.left - containerRect.left - gapSize;
      const scrollPosition =
        containerScrollLeft + targetLeft;

      container.scrollTo({
        left: Math.ceil(scrollPosition),
        behavior: "smooth",
      });
    },
    [],
  );

  const arrows = useMemo(() => {
    return (
      <>
        <ArrowButton
          disabled={leftArrowDisabled}
          onClick={() => handleScroll("left")}
          type={ArrowButtonType.LEFT}
          aria-label={"Prev"}
        />
        <ArrowButton
          disabled={rightArrowDisabled}
          onClick={() => handleScroll("right")}
          type={ArrowButtonType.RIGHT}
          aria-label={"Next"}
        />
      </>
    );
  }, [handleScroll, leftArrowDisabled, rightArrowDisabled]);
  return (
    <Container
      className="bg-color-linen"
      backgroundColor={backgroundColor}
      color={color}
    >
      <TextContainer>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <DesktopScrollButtonsContainer>
          {arrows}
        </DesktopScrollButtonsContainer>
      </TextContainer>
      <CardsContainer>
        <ScrollContainer
          vertical={false}
          className="scroll-container"
          innerRef={scrollerRef}
          nativeMobileScroll={false}
          hideScrollbars={true}
          activationDistance={5}
          onScroll={() => {
            if (!scrollerRef.current) return;
            const container = scrollerRef.current;
            const containerRect =
              container.getBoundingClientRect();
            const cards = Array.from(container.children);

            const firstCard = cards[0];
            const lastCard = cards[cards.length - 1];

            if (firstCard && lastCard) {
              const firstRect =
                firstCard.getBoundingClientRect();
              const lastRect =
                lastCard.getBoundingClientRect();

              setLeftArrowDisabled(
                firstRect.left >= containerRect.left,
              );
              setRightArrowDisabled(
                lastRect.right <= containerRect.right,
              );
            }
          }}
        >
          {cards.map((card) => (
            <Card
              className="ignore hover-bg-color-butterleaf"
              href={card.href}
              key={card.id}
              backgroundColor={cardsBackgroundColor}
              hoverBackgroundColor={cardsAcBackgroundColor}
              aria-label={`navigate to ${card.fullName}'s page${card.linkIsExternal ? ' in a new tab' : ''}`}
              target={card.linkIsExternal ? "_blank" : "_self"}
              rel={card.linkIsExternal ? "noopener noreferrer" : undefined}
            >
              <CardImage src={card.image} />
              <CardLowerContainer>
                <CardText
                  dangerouslySetInnerHTML={{
                    __html: card.text,
                  }}
                />
                <InteractiveContainer>
                  <CardTitle>
                    <CardTitleLine
                      dangerouslySetInnerHTML={{
                        __html: card.fullName,
                      }}
                    />
                    {card.role && 
                    <CardTitleLine
                      dangerouslySetInnerHTML={{
                        __html: card.role,
                      }}
                    />
                    }
                  </CardTitle>
                  <IconContainer>
                    <Icon src={card.linkIsExternal ? DiagonalArrow : RightArrow} />
                  </IconContainer>
                </InteractiveContainer>
              </CardLowerContainer>
            </Card>
          ))}
        </ScrollContainer>
      </CardsContainer>
      <MobileScrollButtonsContainer>
        {arrows}
      </MobileScrollButtonsContainer>
    </Container>
  );
};

export default VideoCardsCarousel;
